"use client";

import { useState, useEffect } from "react";
import { Card, CardBody } from "@heroui/card";
import { Input } from "@heroui/input";
import { Button } from "@heroui/button";
import { Lock } from "lucide-react";
import { motion } from "framer-motion";
import { IS_PROD } from "@/config/constants";

const CORRECT_PASSWORD = "spicenet2025";
const STORAGE_KEY = "spicenet-auth";

// Check if we're in staging environment

export function PasswordProtection({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (IS_PROD) {
      setIsAuthenticated(true);
      setIsLoading(false);
      return;
    }

    // Check if already authenticated in session storage
    const storedAuth = sessionStorage.getItem(STORAGE_KEY);
    if (storedAuth === "true") {
      setIsAuthenticated(true);
    }
    setIsLoading(false);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (password === CORRECT_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem(STORAGE_KEY, "true");
      setError(false);
    } else {
      setError(true);
      setPassword("");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="animate-pulse">
          <div className="w-12 h-12 bg-spicenet-primary rounded-full mx-auto" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="w-full max-w-md shadow-2xl">
            <CardBody className="p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-spicenet-primary to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Lock className="w-10 h-10 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  spicenet Portal
                </h1>
                <p className="text-gray-600">Enter password to continue</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  type="password"
                  label="Password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  isInvalid={error}
                  errorMessage={error ? "Incorrect password" : ""}
                  classNames={{
                    input: "text-gray-900",
                    label: "text-gray-700",
                  }}
                  autoFocus
                />

                <Button
                  type="submit"
                  className="w-full bg-spicenet-primary hover:bg-red-600 text-white"
                  size="lg"
                >
                  Unlock
                </Button>
              </form>
            </CardBody>
          </Card>
        </motion.div>
      </div>
    );
  }

  return <>{children}</>;
}
